import type { JourneyItem } from "@/lib/content.functions";
import type { DescentConfig } from "@/lib/descent";
import type { HydratedNode } from "@/components/DescentEngine";

/**
 * Turn config nodes into renderable nodes using the fetched discoveries.
 * Discovery nodes whose article is missing or unpublished are dropped
 * rather than rendered as dead placeholders.
 */
export function hydrateNodes(
  config: DescentConfig,
  items: Array<JourneyItem | null | undefined>,
): HydratedNode[] {
  const bySlug = new Map<string, JourneyItem>();
  for (const item of items) if (item && !bySlug.has(item.slug)) bySlug.set(item.slug, item);
  return config.nodes.flatMap((n): HydratedNode[] => {
    if (n.type === "marker") {
      return [{ ...n, label: n.label ?? "", type: "marker" as const }];
    }
    const item = n.slug ? bySlug.get(n.slug) : undefined;
    if (!item) return [];
    return [
      {
        depthMeters: n.depthMeters,
        type: "discovery" as const,
        slug: item.slug,
        label: n.label || item.title,
        subtext: n.subtext ?? item.excerpt,
        imageUrl: item.image_url,
      },
    ];
  });
}

/** Every discovery slug a journey wants to render. */
export function journeySlugs(config: DescentConfig): string[] {
  return Array.from(
    new Set(config.nodes.filter((n) => n.type === "discovery" && n.slug).map((n) => n.slug!)),
  );
}
